import { useState, useEffect } from 'react';
import { 
  Printer, Wifi, WifiOff, Usb, Settings, RefreshCw, 
  CheckCircle2, XCircle, Send, AlertCircle, Bluetooth
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import printBridge from '../utils/printBridge';

export default function PrintSettings() {
  const [connected, setConnected] = useState(false);
  const [connectionType, setConnectionType] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [defaultPrinter, setDefaultPrinter] = useState(null);
  const [bridgeUrl, setBridgeUrl] = useState('ws://localhost:9100');
  const [connecting, setConnecting] = useState(false);
  const [testPrinting, setTestPrinting] = useState(false);
  const [bluetoothSupported, setBluetoothSupported] = useState(false);
  
  // Settings
  const [settings, setSettings] = useState({
    autoPrintReceipt: true,
    autoPrintKitchen: true,
    openCashDrawer: true,
    receiptCopies: 1,
    kitchenCopies: 1,
  });

  useEffect(() => {
    // Check Bluetooth support
    setBluetoothSupported('bluetooth' in navigator);
    
    // Set up event listeners
    printBridge.on('connected', handleConnected);
    printBridge.on('disconnected', handleDisconnected);
    printBridge.on('printers', handlePrinters);
    printBridge.on('print_success', handlePrintSuccess);
    printBridge.on('print_error', handlePrintError);

    // Check if already connected
    setConnected(printBridge.isConnected());
    setConnectionType(printBridge.getConnectionType());
    setPrinters(printBridge.getPrinters());
    setDefaultPrinter(printBridge.getDefaultPrinter());

    return () => {
      printBridge.off('connected', handleConnected);
      printBridge.off('disconnected', handleDisconnected);
      printBridge.off('printers', handlePrinters);
      printBridge.off('print_success', handlePrintSuccess);
      printBridge.off('print_error', handlePrintError);
    };
  }, []);

  const handleConnected = (data) => {
    setConnected(true);
    setConnectionType(data.type);
    setConnecting(false);
    toast.success(`Connected via ${data.type.toUpperCase()}`);
  };

  const handleDisconnected = () => {
    setConnected(false);
    setConnectionType(null);
    setPrinters([]);
    setDefaultPrinter(null);
  };

  const handlePrinters = (printerList) => {
    setPrinters(printerList);
    if (printerList.length > 0 && !defaultPrinter) {
      setDefaultPrinter(printerList[0]);
    }
  };

  const handlePrintSuccess = () => {
    setTestPrinting(false);
    toast.success('Print job sent successfully');
  };

  const handlePrintError = (error) => {
    setTestPrinting(false);
    toast.error(`Print error: ${error.message || 'Unknown error'}`);
  };

  const handleConnectWebSocket = async () => {
    setConnecting(true);
    try {
      await printBridge.connectWebSocket(bridgeUrl);
    } catch (error) {
      toast.error(`Connection failed: ${error.message}`);
      setConnecting(false);
    }
  };

  const handleConnectUSB = async () => {
    setConnecting(true);
    try {
      await printBridge.connectUSB();
      setConnecting(false);
    } catch (error) {
      toast.error(`USB connection failed: ${error.message}`);
      setConnecting(false);
    }
  };

  const handleConnectBluetooth = async () => {
    setConnecting(true);
    try {
      await printBridge.connectBluetooth();
      setConnecting(false);
    } catch (error) {
      toast.error(`Bluetooth connection failed: ${error.message}`);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    await printBridge.disconnect();
    toast.info('Disconnected from printer');
  };

  const handleTestPrint = async () => {
    if (!connected) {
      toast.error('Not connected to printer');
      return;
    }
    
    setTestPrinting(true);
    try {
      await printBridge.printTestPage();
    } catch (error) {
      toast.error(`Test print failed: ${error.message}`);
      setTestPrinting(false);
    }
  };

  const handleOpenCashDrawer = async () => {
    if (!connected) {
      toast.error('Not connected to printer');
      return;
    }
    
    try {
      await printBridge.openCashDrawer();
      toast.success('Cash drawer opened');
    } catch (error) {
      toast.error(`Failed to open drawer: ${error.message}`);
    }
  };

  const handleSetDefaultPrinter = (printerId) => {
    if (printBridge.setDefaultPrinter(printerId)) {
      const printer = printers.find(p => p.id === printerId);
      setDefaultPrinter(printer);
      toast.success(`Default printer set to ${printer.name}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="print-settings">
      {/* Connection Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5" />
            Print Bridge Status
          </CardTitle>
          <CardDescription>
            Connect to thermal printers for receipt and kitchen ticket printing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {connected ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Connected</span>
                  <Badge variant="outline" className="ml-2">
                    {connectionType === 'websocket' ? 'Network' : 
                     connectionType === 'bluetooth' ? 'Bluetooth' : 'USB'}
                  </Badge>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-slate-500">
                  <XCircle className="w-5 h-5" />
                  <span>Not Connected</span>
                </div>
              )}
            </div>
            
            {connected ? (
              <Button variant="outline" onClick={handleDisconnect}>
                Disconnect
              </Button>
            ) : null}
          </div>

          {/* Connection Options */}
          {!connected && (
            <div className="grid gap-4 md:grid-cols-3">
              {/* WebSocket Connection */}
              <Card className="border-2 border-dashed">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Wifi className="w-5 h-5 text-blue-500" />
                    <span className="font-medium">Network (Print Server)</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-3">
                    Connect to a local print server running on your network
                  </p>
                  <div className="space-y-2">
                    <Label>Server URL</Label>
                    <Input
                      value={bridgeUrl}
                      onChange={(e) => setBridgeUrl(e.target.value)}
                      placeholder="ws://localhost:9100"
                    />
                  </div>
                  <Button 
                    className="w-full mt-3" 
                    onClick={handleConnectWebSocket}
                    disabled={connecting}
                  >
                    {connecting ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Wifi className="w-4 h-4 mr-2" />
                    )}
                    Connect
                  </Button>
                </CardContent>
              </Card>

              {/* Bluetooth Connection */}
              <Card className="border-2 border-dashed">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Bluetooth className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Bluetooth</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-3">
                    Connect to Bluetooth thermal printers wirelessly
                  </p>
                  {bluetoothSupported ? (
                    <>
                      <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700 mb-3">
                        <CheckCircle2 className="w-4 h-4 inline mr-1" />
                        Bluetooth supported
                      </div>
                      <Button 
                        className="w-full" 
                        variant="outline"
                        onClick={handleConnectBluetooth}
                        disabled={connecting}
                      >
                        {connecting ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Bluetooth className="w-4 h-4 mr-2" />
                        )}
                        Pair Printer
                      </Button>
                    </>
                  ) : (
                    <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Bluetooth not supported. Use Chrome or Edge browser.
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* USB Connection */}
              <Card className="border-2 border-dashed">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Usb className="w-5 h-5 text-green-500" />
                    <span className="font-medium">Direct USB</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-3">
                    Connect directly to a USB printer (Chrome only)
                  </p>
                  <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700 mb-3">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    Requires Chrome browser and printer driver
                  </div>
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={handleConnectUSB}
                    disabled={connecting}
                  >
                    <Usb className="w-4 h-4 mr-2" />
                    Select USB Printer
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Printers */}
      {connected && (
        <Card>
          <CardHeader>
            <CardTitle>Available Printers</CardTitle>
          </CardHeader>
          <CardContent>
            {printers.length === 0 ? (
              <p className="text-slate-500">No printers found</p>
            ) : (
              <div className="space-y-2">
                {printers.map((printer) => (
                  <div 
                    key={printer.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      defaultPrinter?.id === printer.id ? 'border-orange-500 bg-orange-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Printer className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="font-medium">{printer.name}</p>
                        <p className="text-sm text-slate-500">{printer.type}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {defaultPrinter?.id === printer.id ? (
                        <Badge className="bg-orange-500">Default</Badge>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleSetDefaultPrinter(printer.id)}
                        >
                          Set Default
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-2 mt-4">
              <Button onClick={handleTestPrint} disabled={testPrinting}>
                {testPrinting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Test Print
              </Button>
              <Button variant="outline" onClick={handleOpenCashDrawer}>
                Open Cash Drawer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Print Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Print Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Auto-print receipts</p>
              <p className="text-sm text-slate-500">
                Print receipt when payment is completed
              </p>
            </div>
            <Switch
              checked={settings.autoPrintReceipt}
              onCheckedChange={(checked) => 
                setSettings(s => ({ ...s, autoPrintReceipt: checked }))
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Auto-print kitchen tickets</p>
              <p className="text-sm text-slate-500">
                Print kitchen ticket when order is placed
              </p>
            </div>
            <Switch
              checked={settings.autoPrintKitchen}
              onCheckedChange={(checked) => 
                setSettings(s => ({ ...s, autoPrintKitchen: checked }))
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Open cash drawer on cash sale</p>
              <p className="text-sm text-slate-500">
                Automatically kick open cash drawer
              </p>
            </div>
            <Switch
              checked={settings.openCashDrawer}
              onCheckedChange={(checked) => 
                setSettings(s => ({ ...s, openCashDrawer: checked }))
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="space-y-2">
              <Label>Receipt copies</Label>
              <Select 
                value={settings.receiptCopies.toString()}
                onValueChange={(v) => setSettings(s => ({ ...s, receiptCopies: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 copy</SelectItem>
                  <SelectItem value="2">2 copies</SelectItem>
                  <SelectItem value="3">3 copies</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Kitchen ticket copies</Label>
              <Select 
                value={settings.kitchenCopies.toString()}
                onValueChange={(v) => setSettings(s => ({ ...s, kitchenCopies: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 copy</SelectItem>
                  <SelectItem value="2">2 copies</SelectItem>
                  <SelectItem value="3">3 copies</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Setup Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="prose prose-sm max-w-none">
            <h4 className="font-medium">Option 1: Bluetooth (Best for Mobile/Tablets)</h4>
            <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1">
              <li>Ensure your Bluetooth printer is powered on and in pairing mode</li>
              <li>Click "Pair Printer" and select your printer from the list</li>
              <li>Wait for the connection to establish</li>
              <li>Works great as fallback for offline mode</li>
            </ol>

            <h4 className="font-medium mt-4">Option 2: Network Print Server (Recommended for Fixed POS)</h4>
            <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1">
              <li>Download the Print Bridge server for your OS</li>
              <li>Connect your thermal printer via USB to the server computer</li>
              <li>Run the Print Bridge server (it will listen on port 9100)</li>
              <li>Enter the server URL above and click Connect</li>
            </ol>

            <h4 className="font-medium mt-4">Option 3: Direct USB (Chrome only)</h4>
            <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1">
              <li>Connect your thermal printer directly to this computer</li>
              <li>Click "Select USB Printer" button</li>
              <li>Choose your printer from the popup</li>
            </ol>

            <h4 className="font-medium mt-4">Offline Mode with Local Sync</h4>
            <p className="text-sm text-slate-600 mb-2">
              Waiters can connect their devices to the main POS computer via local network when internet is down:
            </p>
            <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1">
              <li>Main POS starts as "Host" on the local network</li>
              <li>Waiter tablets auto-discover and connect to the host</li>
              <li>Orders sync between all devices locally</li>
              <li>When internet returns, all orders sync to the cloud</li>
            </ol>

            <h4 className="font-medium mt-4">Supported Printers</h4>
            <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
              <li>Epson TM series (T20, T88, etc.)</li>
              <li>Star Micronics TSP series</li>
              <li>Citizen CT series</li>
              <li>Bixolon SRP series</li>
              <li>Any ESC/POS compatible printer (USB or Bluetooth)</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
